/**
 * Trashcan Utilities
 *
 * Functions for soft-deleting and restoring Database/Automation blocks.
 *
 * @module trashcan/trashcan-utils
 */

import { parseXYWH, serializeXYWH } from '@blocksuite/global/gfx';
import type { GfxController } from '@blocksuite/std/gfx';
import { Slice, type Store } from '@blocksuite/store';
import type { TriggerType } from '@streamlain/automation-safety-contract';
import { FILE_INDEX_MANAGED_BY } from '@streamlain/database-identity';

import type {
  TrashcanBlockModel,
  TrashcanItem,
} from '../schemas/trashcan-model.js';
import { peekSystemWorkspaceStore } from '../system-workspace/system-workspace-store.js';

// =============================================================================
// Constants
// =============================================================================

/** Block types that support soft-delete */
const TRASHABLE_FLAVOURS = [
  'affine:database',
  'affine:automation',
  'affine:elster',
] as const;

/** 30 days in milliseconds */
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

/** Backend API base URL */
const API_BASE_URL = '/api/workflow';

// =============================================================================
// Trigger Management (for Automations)
// =============================================================================

interface TriggerConfig {
  type: TriggerType;
  enabled: boolean;
  cronPreset?: string;
}

/**
 * Deactivate backend trigger for an automation block.
 * First checks if config exists, then trashes it if found.
 * This avoids 404 console errors for blocks that were never synced.
 */
async function deactivateBackendTrigger(
  store: Store,
  blockId: string
): Promise<void> {
  try {
    // Get workspace and doc IDs from store
    const workspace = (store as { workspace?: { id?: string } }).workspace;
    const workspaceId = workspace?.id || 'local';
    const docId = store.id || 'unknown';

    const queryParams = `workspaceId=${encodeURIComponent(workspaceId)}&docId=${encodeURIComponent(docId)}`;

    // 1. Check if config exists (GET doesn't log 404 as browser error)
    const checkUrl = `${API_BASE_URL}/configs/${blockId}?${queryParams}`;
    const checkResponse = await fetch(checkUrl, { method: 'GET' });

    if (!checkResponse.ok) {
      // Config doesn't exist - nothing to trash
      console.log(
        `[Trashcan] No backend config for block ${blockId} (not synced)`
      );
      return;
    }

    // 2. Config exists - trash it
    const trashUrl = `${API_BASE_URL}/configs/${blockId}/trash?${queryParams}`;
    const trashResponse = await fetch(trashUrl, { method: 'POST' });

    if (trashResponse.ok) {
      console.log(`[Trashcan] Trashed backend config for block ${blockId}`);
    } else {
      console.warn(
        `[Trashcan] Failed to trash config for block ${blockId}: ${trashResponse.status}`
      );
    }
  } catch (e) {
    // Network error - ignore silently
    console.log(`[Trashcan] Could not reach backend for block ${blockId}`);
  }
}

/**
 * Reactivate backend config for a restored automation block.
 * First checks if a trashed config exists, then restores it if found.
 * If no config exists, Y.js sync will create it on next block update.
 */
async function reactivateBackendTrigger(
  store: Store,
  blockId: string,
  _triggerConfig: TriggerConfig,
  _webhookUrl: string
): Promise<void> {
  try {
    const workspace = (store as { workspace?: { id?: string } }).workspace;
    const workspaceId = workspace?.id || 'local';
    const docId = store.id || 'unknown';

    const queryParams = `workspaceId=${encodeURIComponent(workspaceId)}&docId=${encodeURIComponent(docId)}`;

    // 1. Check if config exists (might be trashed or might not exist at all)
    const checkUrl = `${API_BASE_URL}/configs/${blockId}?${queryParams}`;
    const checkResponse = await fetch(checkUrl, { method: 'GET' });

    if (!checkResponse.ok) {
      // Config doesn't exist - Y.js sync will create it on next update
      console.log(
        `[Trashcan] No backend config for block ${blockId} - Y.js will sync`
      );
      return;
    }

    // 2. Check if config is trashed (needs restore)
    const config = await checkResponse.json();
    if (config.status !== 'trashed') {
      // Config exists and is not trashed - nothing to do
      console.log(
        `[Trashcan] Backend config for block ${blockId} is active (status: ${config.status})`
      );
      return;
    }

    // 3. Config is trashed - restore it
    const restoreUrl = `${API_BASE_URL}/configs/${blockId}/restore?${queryParams}`;
    const restoreResponse = await fetch(restoreUrl, { method: 'POST' });

    if (restoreResponse.ok) {
      console.log(`[Trashcan] Restored backend config for block ${blockId}`);
    } else {
      console.warn(
        `[Trashcan] Failed to restore config for block ${blockId}: ${restoreResponse.status}`
      );
    }
  } catch (e) {
    // Network error
    console.log(`[Trashcan] Could not reach backend for block ${blockId}`);
  }
}

// =============================================================================
// Ghost Position Detection
// =============================================================================

interface GhostPosition {
  centerX: number;
  centerY: number;
}

/**
 * Find a ghost block that points to the given trashed item.
 * Returns the center position of the ghost if found.
 *
 * This allows restoring blocks at the position where the user
 * placed the ghost, rather than at the viewport center.
 */
function findGhostPositionForItem(
  store: Store,
  itemId: string
): GhostPosition | null {
  const ghosts = store.getBlocksByFlavour('affine:ghost');

  for (const ghostBlock of ghosts) {
    const props = ghostBlock.model?.props as {
      targetDatabaseId?: string;
      xywh?: string;
    };

    // Check if this ghost points to the item being restored
    if (props?.targetDatabaseId === itemId && props?.xywh) {
      const xywh = parseXYWH(props.xywh);
      if (!xywh) continue;
      const [x, y, w, h] = xywh;
      return {
        centerX: x + w / 2,
        centerY: y + h / 2,
      };
    }
  }

  return null;
}

// =============================================================================
// Trashcan Access
// =============================================================================

/**
 * Get or create the trashcan block in the document
 */
export function getOrCreateTrashcan(store: Store): TrashcanBlockModel | null {
  // Find existing trashcan
  const existing = store.getBlocksByFlavour('affine:trashcan')[0];
  if (existing) {
    return existing.model as TrashcanBlockModel;
  }

  // Create new trashcan under root
  const rootId = store.root?.id;
  if (!rootId) {
    console.warn('Cannot create trashcan: no root block');
    return null;
  }

  try {
    const id = store.addBlock('affine:trashcan', { items: [] }, rootId);
    const block = store.getBlock(id);
    return block?.model as TrashcanBlockModel;
  } catch (e) {
    console.error('Failed to create trashcan block:', e);
    return null;
  }
}

/**
 * Get the trashcan block if it exists
 */
export function getTrashcan(store: Store): TrashcanBlockModel | null {
  const existing = store.getBlocksByFlavour('affine:trashcan')[0];
  return (existing?.model as TrashcanBlockModel) ?? null;
}

// =============================================================================
// Soft Delete
// =============================================================================

/**
 * Check if a block type supports soft-delete
 */
export function isTrashable(flavour: string): boolean {
  return TRASHABLE_FLAVOURS.includes(
    flavour as (typeof TRASHABLE_FLAVOURS)[number]
  );
}

/**
 * Sharded database identity carried into the trashcan so discovery and
 * permanent delete can address the storage documents after the host block is
 * gone.
 */
export interface DatabaseTrashcanIdentity {
  databaseId: string;
  databaseRootDocId?: string;
}

/**
 * Read the sharded database identity from a host database block model.
 */
export function readDatabaseTrashcanIdentity(
  model: unknown
): DatabaseTrashcanIdentity {
  const m = model as {
    id: string;
    props?: { databaseId?: unknown; databaseRootDocId?: unknown };
  };
  const databaseId =
    typeof m.props?.databaseId === 'string' && m.props.databaseId.trim()
      ? m.props.databaseId
      : m.id;
  const databaseRootDocId =
    typeof m.props?.databaseRootDocId === 'string' &&
    m.props.databaseRootDocId.trim()
      ? m.props.databaseRootDocId
      : undefined;
  return { databaseId, databaseRootDocId };
}

/**
 * Resolve the user-facing title of a sharded database from its root system
 * document. The host block only carries a provisioned placeholder title; the
 * real title lives on the storage block inside the root document.
 *
 * Returns `undefined` when the root document is not registered in the
 * workspace, when the database keeps its storage inline in the host document,
 * or when the storage block has no non-empty title.
 */
export function resolveDatabaseRootTitle(
  hostStore: Store,
  identity: DatabaseTrashcanIdentity
): string | undefined {
  const rootDocId = identity.databaseRootDocId;
  if (!rootDocId || rootDocId === hostStore.id) return undefined;
  const rootStore = peekSystemWorkspaceStore(hostStore, rootDocId);
  const storageModel = rootStore?.getBlock(identity.databaseId)?.model;
  if (!storageModel || storageModel.flavour !== 'affine:database') {
    return undefined;
  }
  const title = (
    storageModel as { props?: { title?: { toString(): string } } }
  ).props?.title
    ?.toString()
    .trim();
  return title ? title : undefined;
}

/**
 * Guard consulted before a database block is soft-deleted through
 * {@link requestMoveToTrashcan}. The app layer registers a guard that can show
 * a confirmation dialog listing relation dependencies. Resolves `true` when
 * the delete may proceed.
 */
export type DatabaseTrashGuard = (request: {
  store: Store;
  blockId: string;
  identity: DatabaseTrashcanIdentity;
  displayName: string;
}) => Promise<boolean>;

let databaseTrashGuard: DatabaseTrashGuard | null = null;

/**
 * Register (or clear with `null`) the app-level database trash guard.
 */
export function setDatabaseTrashGuard(guard: DatabaseTrashGuard | null): void {
  databaseTrashGuard = guard;
}

/**
 * Outcome of {@link requestMoveToTrashcan}:
 * - `moved`: the block is in the trashcan (or was a protected no-op that must
 *   not be hard-deleted).
 * - `cancelled`: the user declined in the guard dialog.
 * - `rejected`: the block is not trashable or the trashcan write failed.
 */
export type TrashcanMoveOutcome = 'moved' | 'cancelled' | 'rejected';

/**
 * Soft-delete a block after consulting the database trash guard.
 *
 * Non-database blocks and databases without a registered guard are trashed
 * immediately.
 */
export async function requestMoveToTrashcan(
  store: Store,
  blockId: string,
  options: { allowProtected?: boolean } = {}
): Promise<TrashcanMoveOutcome> {
  const block = store.getBlock(blockId);
  if (!block) return 'rejected';
  if (block.model.flavour === 'affine:database' && databaseTrashGuard) {
    const identity = readDatabaseTrashcanIdentity(block.model);
    const displayName =
      resolveDatabaseRootTitle(store, identity) ??
      getBlockDisplayName(block.model);
    const allowed = await databaseTrashGuard({
      store,
      blockId,
      identity,
      displayName,
    });
    if (!allowed) return 'cancelled';
    if (!store.getBlock(blockId)) return 'rejected';
  }
  return moveToTrashcan(store, blockId, options) ? 'moved' : 'rejected';
}

/**
 * Move a block to the trashcan (soft-delete)
 *
 * @returns true if handled, false if should be deleted normally
 */
export function moveToTrashcan(
  store: Store,
  blockId: string,
  options: { allowProtected?: boolean } = {}
): boolean {
  const block = store.getBlock(blockId);
  if (!block) {
    console.warn('Block not found:', blockId);
    return false;
  }

  const flavour = block.model?.flavour;
  if (!flavour || !isTrashable(flavour)) {
    return false;
  }

  const databaseProtection =
    flavour === 'affine:database'
      ? readDatabaseBlockProtection(block.model)
      : null;
  if (databaseProtection?.fileIndexManaged) {
    console.warn('File Index database is doc-scoped and cannot be deleted.');
    return true;
  }
  if (databaseProtection?.protected && !options.allowProtected) {
    console.warn(
      'Protected database needs explicit delete confirmation:',
      blockId
    );
    return true;
  }

  const trashcan = getOrCreateTrashcan(store);
  if (!trashcan) {
    console.warn('Could not get/create trashcan');
    return false;
  }

  try {
    // Create snapshot of the block
    const slice = Slice.fromModels(store, [block.model]);
    const transformer = store.getTransformer();
    const snapshot = transformer.sliceToSnapshot(slice);

    if (!snapshot) {
      console.error('Failed to create block snapshot');
      return false;
    }

    const databaseIdentity =
      flavour === 'affine:database'
        ? readDatabaseTrashcanIdentity(block.model)
        : null;
    const displayName =
      (databaseIdentity && resolveDatabaseRootTitle(store, databaseIdentity)) ||
      getBlockDisplayName(block.model);

    // Add to trashcan
    const blockTypeMap: Record<string, TrashcanItem['blockType']> = {
      'affine:database': 'database',
      'affine:automation': 'automation',
      'affine:elster': 'elster',
    };
    const item: TrashcanItem = {
      id: blockId,
      blockType: blockTypeMap[flavour] ?? 'automation',
      snapshot,
      deletedAt: Date.now(),
      displayName,
      protected: databaseProtection?.protected,
      managedBy: databaseProtection?.managedBy,
      databaseId: databaseIdentity?.databaseId,
      databaseRootDocId: databaseIdentity?.databaseRootDocId,
    };

    const items = [...trashcan.props.items, item];
    store.updateBlock(trashcan, { items });

    // Deactivate backend trigger for automation blocks (fire and forget)
    if (flavour === 'affine:automation') {
      deactivateBackendTrigger(store, blockId).catch(() => {
        // Ignore errors - trigger deactivation is best-effort
      });
    }

    // Delete the original block
    store.deleteBlock(block.model);

    // Notify the app layer (toast with Undo / Open trash). Event-based so this
    // blocksuite package keeps no dependency on app notification/i18n. Fires
    // only on a successful user-initiated soft-delete — not on protected/
    // file-index no-ops or programmatic block removals.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('streamlain:block-trashed', {
          detail: { blockId, blockType: item.blockType, displayName },
        })
      );
    }

    console.log('Moved block to trashcan:', blockId, displayName);
    return true;
  } catch (e) {
    console.error('Failed to move block to trashcan:', e);
    return false;
  }
}

function readDatabaseBlockProtection(model: unknown): {
  protected: boolean;
  fileIndexManaged: boolean;
  managedBy?: string;
} {
  const props =
    ((model as { props?: Record<string, unknown> } | null)?.props as
      | Record<string, unknown>
      | undefined) ?? {};
  const managedBy =
    typeof props.managedBy === 'string' ? props.managedBy : undefined;
  const fileIndexManaged = managedBy === FILE_INDEX_MANAGED_BY;
  return {
    fileIndexManaged,
    protected:
      props.schemaLocked === true ||
      props.systemManaged === true ||
      managedBy !== undefined,
    managedBy,
  };
}

function isProtectedTrashcanItem(item: TrashcanItem): boolean {
  return item.blockType === 'database' && item.protected === true;
}

/**
 * Get display name for a block
 */
function getBlockDisplayName(model: unknown): string {
  const m = model as {
    props?: { title?: { toString(): string } };
    id?: string;
  };

  // Try title (automation + database)
  if (m.props?.title?.toString) {
    const title = m.props.title.toString();
    if (title.trim()) return title;
  }

  // Fallback to Untitled
  return 'Untitled';
}

// =============================================================================
// Restore
// =============================================================================

/**
 * Restore a block from the trashcan
 *
 * @param store - The document store
 * @param gfx - The GFX controller (for viewport center)
 * @param itemId - The ID of the trashcan item to restore
 * @returns The new block ID, or null if failed
 */
export async function restoreFromTrashcan(
  store: Store,
  gfx: GfxController,
  itemId: string
): Promise<string | null> {
  const trashcan = getTrashcan(store);
  if (!trashcan) {
    console.warn('Trashcan not found');
    return null;
  }

  const item = trashcan.getItem(itemId);
  if (!item) {
    console.warn('Item not found in trashcan:', itemId);
    return null;
  }

  try {
    // Deserialize snapshot
    const transformer = store.getTransformer();
    const slice = await transformer.snapshotToSlice(
      item.snapshot as Parameters<typeof transformer.snapshotToSlice>[0],
      store,
      store.root?.id
    );

    if (!slice || slice.content.length === 0) {
      console.error('Failed to restore from snapshot');
      return null;
    }

    // Try to find a ghost that points to this trashed item
    // If found, use the ghost's center position for the restored block
    let centerX: number;
    let centerY: number;
    const ghostPosition = findGhostPositionForItem(store, itemId);

    if (ghostPosition) {
      centerX = ghostPosition.centerX;
      centerY = ghostPosition.centerY;
      console.log(
        `[Trashcan] Using ghost position for restored block: (${centerX}, ${centerY})`
      );
    } else {
      // Fallback to viewport center
      const viewport = gfx.viewport;
      centerX = viewport.centerX;
      centerY = viewport.centerY;
    }

    // The restored block should be the first in the slice
    const restoredModel = slice.content[0];
    if (!restoredModel) {
      console.warn('Restored slice has no content');
      return null;
    }
    const newBlockId = restoredModel.id;
    if (newBlockId !== item.id) {
      const importedModel = store.getBlock(newBlockId)?.model;
      if (importedModel) {
        store.deleteBlock(importedModel);
      }
      console.error(
        `[Trashcan] Restore changed block id from "${item.id}" to "${newBlockId}".`
      );
      return null;
    }

    // Update position to viewport center using block ID
    // Note: restoredModel is a DraftModel, we need to get the actual BlockModel
    const actualModel = store.getBlock(newBlockId)?.model;
    if (actualModel && 'xywh' in actualModel.props) {
      const xywh = parseXYWH(actualModel.props.xywh);
      if (!xywh) {
        return null;
      }
      const [, , w, h] = xywh;
      const newXywh = serializeXYWH(centerX - w / 2, centerY - h / 2, w, h);
      store.updateBlock(newBlockId, { xywh: newXywh });
    }

    // Remove from trashcan
    const items = trashcan.props.items.filter(i => i.id !== itemId);
    store.updateBlock(trashcan, { items });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('streamlain:block-restored', {
          detail: { blockId: newBlockId, blockType: item.blockType },
        })
      );
    }

    // Reactivate backend trigger for automation blocks
    if (item.blockType === 'automation' && actualModel) {
      const props = actualModel.props as {
        triggerConfig?: TriggerConfig;
        workflowId?: string;
        webhookUrlOverride?: string;
      };
      const triggerConfig = props.triggerConfig;
      const webhookUrl = props.webhookUrlOverride || '';

      if (triggerConfig && webhookUrl) {
        reactivateBackendTrigger(
          store,
          newBlockId,
          triggerConfig,
          webhookUrl
        ).catch(() => {
          // Ignore errors - trigger reactivation is best-effort
        });
      }
    }

    console.log('Restored block from trashcan:', newBlockId);
    return newBlockId;
  } catch (e) {
    console.error('Failed to restore from trashcan:', e);
    return null;
  }
}

// =============================================================================
// Permanent Delete
// =============================================================================

/**
 * Permanently delete an item from the trashcan
 */
export function permanentDeleteFromTrashcan(
  store: Store,
  itemId: string,
  options: { allowProtected?: boolean } = {}
): boolean {
  const trashcan = getTrashcan(store);
  if (!trashcan) {
    console.warn('Trashcan not found');
    return false;
  }

  const itemExists = trashcan.getItem(itemId);
  if (!itemExists) {
    console.warn('Item not found in trashcan:', itemId);
    return false;
  }
  if (isProtectedTrashcanItem(itemExists) && !options.allowProtected) {
    console.warn(
      'Protected trashcan item needs explicit permanent-delete confirmation:',
      itemId
    );
    return false;
  }

  const items = trashcan.props.items.filter(i => i.id !== itemId);
  store.updateBlock(trashcan, { items });
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('streamlain:block-permanently-deleted', {
        detail: { blockId: itemId, blockType: itemExists.blockType },
      })
    );
  }

  console.log('Permanently deleted from trashcan:', itemId);
  return true;
}

// =============================================================================
// Cleanup
// =============================================================================

/**
 * Remove items older than 30 days from the trashcan
 *
 * @returns Number of items removed
 */
export function cleanupOldTrashItems(store: Store): number {
  const trashcan = getTrashcan(store);
  if (!trashcan || trashcan.props.items.length === 0) {
    return 0;
  }

  const now = Date.now();
  const itemsBefore = trashcan.props.items.length;

  const items = trashcan.props.items.filter(item => {
    const age = now - item.deletedAt;
    if (isProtectedTrashcanItem(item)) return true;
    return age < THIRTY_DAYS_MS;
  });

  if (items.length < itemsBefore) {
    store.updateBlock(trashcan, { items });
    const removed = itemsBefore - items.length;
    console.log(`Auto-cleanup: removed ${removed} old trash items`);
    return removed;
  }

  return 0;
}
